import type { MarketedMembershipPlanType } from '@frontend/api-client';
import type { PromotionDetails } from '@frontend/api-types';
import type { Reducer } from 'react';

export const MembershipWizardStep = {
  paymentCapture: 'paymentCapture',
  selectPlan: 'selectPlan',
} as const;

export type MembershipWizardStep = (typeof MembershipWizardStep)[keyof typeof MembershipWizardStep];

export type MembershipWizardState = {
  paymentError?: string;
  promoCode?: string;
  promotionDetails?: PromotionDetails;
  selectedPlanType?: MarketedMembershipPlanType;
  step: MembershipWizardStep;
};

export type MembershipWizardAction =
  | { error: string; type: 'paymentError' }
  | { planType: MarketedMembershipPlanType; type: 'selectPlan' }
  | { promoCode: string; promotionDetails: PromotionDetails; type: 'applyPromo' }
  | { type: 'back' }
  | { type: 'continueToPayment' }
  | { type: 'dismissPromo' }
  | { type: 'paymentSuccess' }
  | { type: 'reset' }
  | { type: 'submitPayment' };

type ActionType = MembershipWizardAction['type'];

type ExtractAction<T extends ActionType> = Extract<MembershipWizardAction, { type: T }>;

type Handler<T extends ActionType = ActionType> = (
  prev: MembershipWizardState,
  action: ExtractAction<T>,
) => MembershipWizardState;

type HandlersLookup = Partial<{
  [Step in MembershipWizardStep]: Partial<{
    [Action in ActionType]: Handler<Action>;
  }>;
}>;

const DEFAULT_HANDLERS: HandlersLookup = {
  paymentCapture: {
    back: (prev) => ({
      ...prev,
      step: MembershipWizardStep.selectPlan,
    }),

    paymentError: (prev, action) => ({
      ...prev,
      paymentError: action.error,
    }),

    paymentSuccess: (prev) => ({
      ...prev,
    }),

    submitPayment: (prev) => ({
      ...prev,
      paymentError: undefined,
    }),
  },

  selectPlan: {
    applyPromo: (prev, action) => ({
      ...prev,
      promoCode: action.promoCode,
      promotionDetails: action.promotionDetails,
    }),

    continueToPayment: (prev) => {
      if (!prev.selectedPlanType) {
        return prev;
      }
      return {
        ...prev,
        step: MembershipWizardStep.paymentCapture,
      };
    },

    dismissPromo: (prev) => ({
      ...prev,
      promoCode: undefined,
      promotionDetails: undefined,
    }),

    selectPlan: (prev, action) => ({
      ...prev,
      selectedPlanType: action.planType,
    }),
  },
};

const initialState: MembershipWizardState = {
  step: MembershipWizardStep.selectPlan,
};

export const withMembershipStateMachine =
  ({
    handlers = DEFAULT_HANDLERS,
  }: {
    handlers?: HandlersLookup;
  } = {}): Reducer<MembershipWizardState, MembershipWizardAction> =>
  (prev, action) => {
    if (action.type === 'reset') {
      return initialState;
    }

    const stepHandlers = handlers[prev.step];
    const handler = stepHandlers?.[action.type] as Handler | undefined;
    if (!handler) return prev;

    return handler(prev, action);
  };

export const membershipStateMachine = withMembershipStateMachine();

export { initialState as membershipInitialState };
